import { useState, useRef, useEffect } from 'react';
import { resetTracking } from '@/services/tracking';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useEndImpersonation } from '@/hooks/useAdmin';
import { useActiveFilterChips } from '@/hooks/useFilterChips';
import { setAccessToken } from '@/services/api';
import { exitGodModeSafely } from '@/services/agency.service';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { devLog } from '@/utils/logger';
import { toast } from '@/utils/toast';
import { GodModeIndicator } from './GodModeIndicator';
import { ImpersonationBanner } from '@/components/admin/ImpersonationBanner';
import { FiltersIndicator } from './FiltersIndicator';
import { VariationFiltersIndicator } from '@/components/variations/VariationFiltersIndicator';

/**
 * ActiveStatesIndicator
 *
 * On desktop (lg+): renders GodModeIndicator, ImpersonationBanner, FiltersIndicator
 * unchanged, side by side.
 *
 * On mobile (<lg):
 * - 0 or 1 active: renders individual components as-is (their own mobile pills)
 * - 2+ active: renders a single combined pill + dropdown that surfaces all
 *   active states with their exit / clear actions in one place, preventing
 *   right-side icons from being pushed off-screen.
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getTimeRemaining = (expiresAt: string): string => {
  const now = new Date().getTime();
  const expires = new Date(expiresAt).getTime();
  const msRemaining = expires - now;

  if (msRemaining <= 0) return 'Expired';

  const minutesRemaining = Math.floor(msRemaining / 60000);
  const hoursRemaining = Math.floor(minutesRemaining / 60);

  if (hoursRemaining > 0) {
    const remainingMinutes = minutesRemaining % 60;
    return remainingMinutes > 0
      ? `${hoursRemaining}h ${remainingMinutes}m`
      : `${hoursRemaining}h`;
  }

  return `${minutesRemaining}m`;
};

// ---------------------------------------------------------------------------
// Alert SVG (shared)
// ---------------------------------------------------------------------------

const AlertIcon = () => (
  <svg
    className="w-4 h-4 flex-shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    />
  </svg>
);

// ---------------------------------------------------------------------------
// Combined mobile dropdown (2+ active states)
// ---------------------------------------------------------------------------

interface CombinedDropdownProps {
  activeCount: number;
  onClose: () => void;
}

const CombinedDropdown = ({ activeCount, onClose }: CombinedDropdownProps) => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation('common');
  const endImpersonation = useEndImpersonation();
  const [isExitingGodMode, setIsExitingGodMode] = useState(false);

  const godModeSession = user?.godModeSession ?? null;
  const impersonationSession = user?.impersonationSession ?? null;

  // ---------------------------------------------------------------------------
  // God Mode exit
  // ---------------------------------------------------------------------------

  const handleExitGodMode = async () => {
    setIsExitingGodMode(true);
    try {
      await exitGodModeSafely();
    } catch (error) {
      // Genuine failure (network/5xx). Do not trap the user: still clear
      // local state and land on the portfolio below.
      devLog.error('[ActiveStatesIndicator] Failed to exit God Mode:', error);
      toast.error(t('error_exit_god_mode'));
    }
    try {
      await refreshUser();
    } catch (error) {
      devLog.error('[ActiveStatesIndicator] refreshUser failed after exit:', error);
    }
    onClose();
    setIsExitingGodMode(false);
    window.location.href = '/agency';
  };

  // ---------------------------------------------------------------------------
  // Impersonation exit
  // ---------------------------------------------------------------------------

  const handleEndImpersonation = async () => {
    if (!impersonationSession) return;
    try {
      const result = await endImpersonation.mutateAsync(impersonationSession.id);
      await resetTracking();
      setAccessToken(result.accessToken);
      await refreshUser();
      toast.success('Impersonation session ended');
      onClose();
      navigate('/admin');
    } catch (error) {
      devLog.error('[ActiveStatesIndicator] Failed to end impersonation:', error);
      toast.error('Failed to end impersonation session');
    }
  };

  // Filter chips: shared builder (route-aware), SSOT with FiltersIndicator
  const { pathname } = useLocation();
  const { chips: activeFilterChips, clearAll: clearFilters } = useActiveFilterChips(pathname);

  const hasFilters = activeFilterChips.length > 0;

  return (
    <div className="fixed left-4 right-4 top-[4.5rem] bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50">
        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          {activeCount} active states
        </span>
        <button
          onClick={onClose}
          className="p-1 rounded-full hover:bg-gray-200 transition-colors"
          aria-label="Close active states panel"
        >
          <Icon name={ICONS.CLOSE} size="xs" color="text-gray-500" ariaHidden />
        </button>
      </div>

      {/* God Mode section */}
      {godModeSession && (
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-peach text-gray-900 rounded text-[10px] font-bold uppercase tracking-wide">
              <AlertIcon />
              {t('god_mode_viewing')}
            </span>
            <span className="text-sm font-medium text-gray-900 truncate">
              {godModeSession.targetTeamName}
            </span>
          </div>
          <button
            onClick={handleExitGodMode}
            disabled={isExitingGodMode}
            className="w-full px-3 py-2 border border-bs-black text-bs-black rounded-md text-sm font-semibold hover:bg-bs-black hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={t('aria_exit_god_mode')}
          >
            {isExitingGodMode ? '...' : t('god_mode_exit_to_cc')}
          </button>
        </div>
      )}

      {/* Impersonation section */}
      {impersonationSession && (
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500 text-white rounded text-[10px] font-bold uppercase tracking-wide">
              <AlertIcon />
              Impersonating
            </span>
            <span className="text-sm font-medium text-gray-900 truncate">
              {impersonationSession.targetEmail}
            </span>
            {impersonationSession.isReadOnly && (
              <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-semibold">
                RO
              </span>
            )}
            <span className="flex-shrink-0 text-[10px] text-gray-400 ml-auto">
              {getTimeRemaining(impersonationSession.expiresAt)}
            </span>
          </div>
          <button
            onClick={handleEndImpersonation}
            disabled={endImpersonation.isPending}
            className="w-full px-3 py-2 border border-red-500 text-red-600 rounded-md text-sm font-semibold hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="End impersonation session"
          >
            {endImpersonation.isPending ? '...' : 'End Session'}
          </button>
        </div>
      )}

      {/* Filters section */}
      {hasFilters && (
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-soft-mint text-dark-teal rounded text-[10px] font-bold uppercase tracking-wide">
              <Icon name={ICONS.FILTER} size="xs" color="text-dark-teal" ariaHidden />
              Filters ({activeFilterChips.length})
            </span>
            <button
              onClick={() => {
                clearFilters();
                onClose();
              }}
              className="text-xs font-medium text-red-500 hover:text-red-600 transition-colors"
            >
              {t('clear_all')}
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {activeFilterChips.map((chip) => (
              <span
                key={chip.key}
                className="inline-flex items-center gap-1 px-2 py-1 bg-soft-mint text-dark-teal rounded-md text-xs font-medium"
              >
                {chip.label}
                <button
                  onClick={chip.onRemove}
                  className="hover:bg-warm-gray rounded-full p-0.5 transition-colors"
                  aria-label={t('remove_filter', { filter: chip.label })}
                >
                  <Icon name={ICONS.CLOSE} size="xs" color="text-dark-teal" ariaHidden />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const ActiveStatesIndicator = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const activeChips = useActiveFilterChips(pathname);
  const [combinedOpen, setCombinedOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close combined dropdown on outside click
  useEffect(() => {
    if (!combinedOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setCombinedOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [combinedOpen]);

  // Count how many states are currently active
  const godModeActive = !!user?.godModeSession;
  const impersonationActive = !!(user?.impersonationSession);

  const filterCount = activeChips.chips.length;

  const filtersActive = filterCount > 0;
  const activeCount = [godModeActive, impersonationActive, filtersActive].filter(Boolean).length;

  // Desktop: always render individual components unchanged
  // Mobile 0-1 active: individual components (their own mobile pills)
  // Mobile 2+ active: combined pill

  return (
    <>
      {/* Desktop: always individual (lg+) */}
      <div className="hidden lg:contents">
        <GodModeIndicator />
        <ImpersonationBanner />
        {pathname.startsWith('/variations') ? <VariationFiltersIndicator /> : <FiltersIndicator />}
      </div>

      {/* Mobile: conditional (< lg) */}
      <div className="min-w-0 lg:hidden">
        {activeCount < 2 ? (
          // 0 or 1 active — individual components handle their own mobile pills
          <>
            <GodModeIndicator />
            <ImpersonationBanner />
            {pathname.startsWith('/variations') ? <VariationFiltersIndicator /> : <FiltersIndicator />}
          </>
        ) : (
          // 2+ active — single combined pill
          <div ref={containerRef} className="relative">
            <button
              onClick={() => setCombinedOpen(!combinedOpen)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-peach text-gray-900 rounded-lg shadow-md text-xs font-medium whitespace-nowrap flex-shrink-0"
              aria-expanded={combinedOpen}
              aria-haspopup="true"
              aria-label={`${activeCount} active states — tap to view`}
            >
              <Icon name={ICONS.INFO} size="xs" color="text-gray-900" ariaHidden />
              <span className="font-bold">{activeCount}</span>
            </button>

            {combinedOpen && (
              <CombinedDropdown
                activeCount={activeCount}
                onClose={() => setCombinedOpen(false)}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};
